import { TriangleAlert } from "lucide-react";
import * as React from "react";

import { Alert, Avatar, Button, Combobox, Dialog, Field } from "@/components/ui";
import { extractInitials } from "@/utils";
import { User, VideoProject } from "@/services/bedrock";
import {
  assignableEditorUsers,
  isNewSamePersonEditorQaAssignment,
  otherStaffRole,
  resolveStaffUser,
  sortStaffWithCurrentUserFirst,
  staffDisplayName,
  staffRoleLabel,
} from "@/components/VideoProjectStaffPicker";

interface KanbanAssignStaffDialogProps {
  open: boolean;
  role: VideoProject.QueueAssignment["role"];
  staffUsers: User[];
  currentUserId?: number;
  selectedUserId?: number | null;
  otherRoleUserId?: number | null;
  currentRoleUserId?: number | null;
  onSelectedUserIdChange: (userId: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export function KanbanAssignStaffDialog({
  open,
  role,
  staffUsers,
  currentUserId,
  selectedUserId,
  otherRoleUserId,
  currentRoleUserId,
  onSelectedUserIdChange,
  onConfirm,
  onCancel,
}: KanbanAssignStaffDialogProps) {
  const label = staffRoleLabel(role);
  const otherLabel = staffRoleLabel(otherStaffRole(role));
  const selectedUser = resolveStaffUser(selectedUserId, staffUsers);
  const options = React.useMemo(() => {
    const assignable = assignableEditorUsers(staffUsers);
    const withSelected =
      selectedUser && !assignable.some(user => user.id === selectedUser.id)
        ? [selectedUser, ...assignable]
        : assignable;
    return sortStaffWithCurrentUserFirst(withSelected, currentUserId);
  }, [currentUserId, selectedUser, staffUsers]);
  const conflict = isNewSamePersonEditorQaAssignment(selectedUserId, otherRoleUserId, currentRoleUserId);

  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && onCancel()} size="sm">
      <Dialog.Header>
        <Dialog.Title>Assign {label}</Dialog.Title>
      </Dialog.Header>
      <Dialog.Content className="flex flex-col gap-3">
        <Dialog.Description>Who should be the {label} for this project?</Dialog.Description>
        <Field label={label}>
          <Combobox
            className="w-full"
            searchable
            items={options}
            value={selectedUser}
            onValueChange={user => {
              if (user) onSelectedUserIdChange(user.id);
            }}
            placeholder={`Select ${label}`}
            itemToStringLabel={user => {
              const name = staffDisplayName(user);
              return currentUserId != null && user.id === currentUserId ? `${name} (you)` : name;
            }}
            isItemEqualToValue={(current, selected) => current.id === selected.id}
            itemToIcon={user => (
              <Avatar className="size-5 mr-1.5">
                {user.photoUrl ? <Avatar.Image src={user.photoUrl} alt={staffDisplayName(user)} /> : null}
                <Avatar.Fallback className="text-[8px]">{extractInitials(staffDisplayName(user))}</Avatar.Fallback>
              </Avatar>
            )}
          />
        </Field>
        {conflict ? (
          <Alert variant="warning">
            <TriangleAlert />
            <Alert.Title>Same person as Editor and QA</Alert.Title>
            <Alert.Description>
              {selectedUser ? staffDisplayName(selectedUser) : "This user"} is already assigned as {otherLabel}.
            </Alert.Description>
          </Alert>
        ) : null}
      </Dialog.Content>
      <Dialog.Footer>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={selectedUserId == null} onClick={onConfirm}>
          {conflict ? "Assign anyway" : `Assign ${label}`}
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
